import { Box, SxProps, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { mdiChevronRight } from '@mdi/js';

import {
    SimpleAccordion,
    SimpleAccordionSummary,
    SimpleAccordionDetails,
} from 'common/components/SimpleAccordion';
import { Icon } from 'common/components/Icon';

import { DescriptionBox } from '../DescriptionBox/DescriptionBox';
import { DescriptionButton } from '../DescriptionButton';

interface IProps {
    children?: React.ReactNode;
    className?: string;
    defaultExpanded?: boolean;
    description?: React.ReactNode;
    hasError?: boolean;
    // An action node (e.g. a clear button) rendered at the end of the header.
    headerAction?: React.ReactNode;
    // Reduce the vertical padding so the header and content read as one element.
    dense?: boolean;
    renderExpanded?: boolean; // Render children when expanded
    sx?: SxProps;
    title: string;
    onExpand?: () => void;
}

export const FormSection = ({
    children,
    defaultExpanded,
    description,
    hasError,
    headerAction,
    dense,
    renderExpanded,
    sx,
    title,
    onExpand,
}: IProps) => {
    const [expanded, setExpanded] = useState(
        defaultExpanded || hasError || false
    );
    const [descriptionOpen, setDescriptionOpen] = useState(false);

    useEffect(() => {
        if (hasError && !expanded) setExpanded(true);
    }, [hasError, expanded]);

    useEffect(() => {
        if (expanded) onExpand?.();
    }, [expanded, onExpand]);

    const renderChildren = (renderExpanded && expanded) || !renderExpanded;

    return (
        <SimpleAccordion expanded={expanded} sx={sx}>
            <SimpleAccordionSummary
                expandIcon={<Icon path={mdiChevronRight} size="2.4rem" />}
                sx={{
                    pr: 1.2,
                    ...(dense && {
                        '.MuiAccordionSummary-content': { my: 0.5 },
                    }),
                }}
                onClick={() => !hasError && setExpanded(!expanded)}
            >
                <Typography sx={{ fontWeight: 'bold' }}>{title}</Typography>
                {!!description && (
                    <DescriptionButton
                        onClick={(event: React.MouseEvent) => {
                            setDescriptionOpen(
                                expanded ? !descriptionOpen : true
                            );
                            setExpanded(true);
                            event.stopPropagation();
                        }}
                    />
                )}
                {!!headerAction && (
                    <Box sx={{ ml: 'auto' }}>{headerAction}</Box>
                )}
            </SimpleAccordionSummary>
            <SimpleAccordionDetails
                sx={dense ? { pb: { xs: 1, sm: 1 } } : undefined}
            >
                <Box sx={{ flex: '1' }}>
                    {!!description && (
                        <DescriptionBox
                            open={descriptionOpen}
                            onClose={() => setDescriptionOpen(false)}
                        >
                            {description}
                        </DescriptionBox>
                    )}
                    {renderChildren && children}
                </Box>
            </SimpleAccordionDetails>
        </SimpleAccordion>
    );
};
